import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import IconAddPerson from '../../components/icons/IconAddPerson';
import IconAccepted from '../../components/icons/IconAccepted';
import IconCopyUrl from '../../components/icons/IconCopyUrl';
import language from '../../variables/language';
import '../../css/platform-access-modal.css';

// Purpose-built modal (own CSS namespace, not the app-wide .modal-menu/
// .modal-window shell - that one reads too heavy for a small, focused
// dialog like this, per explicit user feedback). Two steps in one modal:
// a compact {email, name} form, then - on success - a "here's the link,
// copy and send it yourself" step, so the teacher never has to go hunting
// for the activation link after the fact.
export default function PlatformAccessModal({ isOpen, studentId, studentName, studentEmail, onClose, onCreated }) {
   const lang = language[language.lang];
   const [email, setEmail] = useState('');
   const [name, setName] = useState('');
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState('');
   const [created, setCreated] = useState(null);
   const [copied, setCopied] = useState(false);

   useEffect(() => {
      if(isOpen) {
         setEmail(studentEmail || '');
         setName(studentName || '');
         setError('');
         setCreated(null);
         setCopied(false);
      }
   }, [isOpen, studentName, studentEmail]);

   if(!isOpen) return '';

   const stopProp = e => e.stopPropagation();

   const handleCreate = () => {
      if(!email.trim() || saving) return;
      setSaving(true);
      setError('');
      axios.post('/api/platform-users', JSON.stringify({ email: email.trim(), name: name.trim(), studentId: String(studentId) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(response => {
         setCreated(response.data);
         onCreated();
      }).catch(err => {
         const data = err.response && err.response.data;
         setError((typeof data === 'string' && data) || (data && data.message) || lang.platformCreateError);
      }).finally(() => setSaving(false));
   };

   const handleCopy = () => {
      if(!created || !created.activationLink) return;
      navigator.clipboard.writeText(created.activationLink).then(() => {
         setCopied(true);
         setTimeout(() => setCopied(false), 1500);
      }).catch(() => {});
   };

   const handleKeyDown = (e) => {
      if(e.key === 'Enter') handleCreate();
   };

   // Rendered via a portal straight onto document.body - the trigger button
   // lives inside .student-card-header, which has its own backdrop-filter
   // (for the header's glass effect). backdrop-filter, like transform,
   // establishes a new containing block for `position: fixed` descendants,
   // so without the portal this overlay would be clipped to the header's
   // own small box instead of covering the viewport - a real bug caught
   // during verification (the modal rendered as a sliver in the top-left
   // corner instead of centered on screen).
   return createPortal((
      <div className='platform-modal-overlay' onClick={onClose}>
         <div className='platform-modal-card' onClick={stopProp}>
            <div className='platform-modal-close' onClick={onClose}>×</div>

            {!created ? (
               <>
                  <div className='platform-modal-badge'>
                     <IconAddPerson style={{ width: 22, height: 22, fill: 'var(--lighttext-color)' }} />
                  </div>
                  <h3 className='platform-modal-title'>{lang.grantPlatformAccess}</h3>
                  <p className='platform-modal-subtitle'>{studentName}</p>

                  <div className='platform-modal-field'>
                     <label className='platform-modal-label'>{lang.platformEmailLabel}</label>
                     <input className='platform-modal-input' type='email' autoFocus
                        value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKeyDown} />
                  </div>
                  <div className='platform-modal-field'>
                     <label className='platform-modal-label'>{lang.platformNameLabel}</label>
                     <input className='platform-modal-input' type='text'
                        value={name} onChange={e => setName(e.target.value)} onKeyDown={handleKeyDown} />
                  </div>

                  {error ? <p className='platform-modal-error'>{error}</p> : ''}

                  <button type='button' className='platform-modal-primary-btn' disabled={!email.trim() || saving} onClick={handleCreate}>
                     {saving ? lang['Loading ...'] : lang.grantPlatformAccessConfirm}
                  </button>
               </>
            ) : (
               <>
                  <div className='platform-modal-badge success'>
                     <IconAccepted style={{ width: 22, height: 22, fill: '#63dac8' }} />
                  </div>
                  <h3 className='platform-modal-title'>{lang.platformAccountCreated}</h3>

                  <div className='platform-modal-link-box'>
                     <span className='platform-modal-link-text'>{created.activationLink}</span>
                     <div className='platform-modal-copy-btn' onClick={handleCopy} title={lang.copyActivationLink}>
                        <IconCopyUrl style={{ width: 15, height: 15, fill: 'var(--bg-color)' }} />
                     </div>
                     {copied ? <span className='platform-modal-copied-hint'>{lang.studentsPage.copied}</span> : ''}
                  </div>

                  <p className='platform-modal-hint'>{lang.platformSendLinkHint}</p>

                  <button type='button' className='platform-modal-primary-btn' onClick={onClose}>{lang.close}</button>
               </>
            )}
         </div>
      </div>
   ), document.body);
}
